import React, { useEffect, useState, useCallback } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import {
  fetchTaxRatesAsync,
  deleteTaxRateAsync,
  clearTaxRateDetails,
} from './taxSlice';
import { TaxRate } from '../../../app/data/models/taxes/taxRate';
import TaxRateForm from './TaxRateForm';
import { Edit, Delete } from '@mui/icons-material';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useRestaurant } from '@app/hooks/useRestaurant';
import LoadingComponent from '@app/layout/LoadingComponent';
import ListTitleBox from '@app/components/list/ListTitleBox';
import EmptyScreen from '@app/components/EmptyScreen';
import { AxiosError } from 'axios';
import ErrorComponent from '@app/layout/ErrorComponent';
import { ConfirmDialog } from '@app/components/ConfirmDialog';
import { LoadingButton } from '@mui/lab';
import TaxesSettings from './TaxesSettings';

export default function TaxRateList() {
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { restaurant, isLoading, error, retry } = useRestaurant(restaurantId!);
  const { taxRates, taxRatesListStatus } = useAppSelector(
    (state) => state.taxes
  );
  const [editMode, setEditMode] = useState(false);
  const [selectedTaxRate, setSelectedTaxRate] = useState<TaxRate | undefined>(
    undefined
  );
  const [deletingTaxRateId, setDeletingTaxRateId] = useState<string | null>(
    null
  );
  const [openConfirm, setOpenConfirm] = useState(false);
  const [taxRateToDelete, setTaxRateToDelete] = useState<string | null>(null);

  // Responsive design with more granular breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (restaurant?.id) {
      dispatch(fetchTaxRatesAsync({ restaurantId: restaurant.id }));
    }
  }, [dispatch, restaurant]);
  const handleSelectTaxRate = (taxRate: TaxRate) => {
    setSelectedTaxRate(taxRate);
    setEditMode(true);
  };

  const cancelEdit = () => {
    dispatch(clearTaxRateDetails());
    setSelectedTaxRate(undefined);
    setEditMode(false);
  };

  const handleFormSuccess = async () => {
    // Refetch the tax rates list after successful form operations
    await dispatch(fetchTaxRatesAsync({ restaurantId: restaurant.id }));
    dispatch(clearTaxRateDetails());
    setSelectedTaxRate(undefined);
    setEditMode(false);
  };

  const handleDeleteTaxRate = useCallback(
    async (taxRateId: string) => {
      setDeletingTaxRateId(taxRateId);
      try {
        const result = await dispatch(
          deleteTaxRateAsync({
            restaurantId: restaurant!.id,
            taxRateId,
          })
        );
        if (deleteTaxRateAsync.fulfilled.match(result)) {
          toast.success(t('taxes.delete.success'));
        }
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          toast.error(
            error.response?.data?.title ||
              error.message ||
              t('taxes.delete.error')
          );
        } else {
          toast.error((error as Error).message || t('taxes.delete.error'));
        }
      } finally {
        setDeletingTaxRateId(null);
      }
    },
    [dispatch, restaurant, t]
  );

  const handleDeleteClick = useCallback((taxRateId: string) => {
    setTaxRateToDelete(taxRateId);
    setOpenConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (taxRateToDelete) {
      await handleDeleteTaxRate(taxRateToDelete);
      setOpenConfirm(false);
      setTaxRateToDelete(null);
    }
  }, [taxRateToDelete, handleDeleteTaxRate]);

  if (isLoading || taxRatesListStatus === 'loading')
    return (
      <LoadingComponent
        header={
          <ListTitleBox
            title={t('taxes.title')}
            primaryButtonText={t('taxes.add-btn')}
            onPrimaryClick={() => setEditMode(true)}
            onBackClick={() => navigate(-1)}
          />
        }
      />
    );
  if (error)
    return (
      <ErrorComponent
        header={
          <ListTitleBox
            title={t('taxes.title')}
            primaryButtonText={t('taxes.add-btn')}
            onPrimaryClick={() => setEditMode(true)}
            onBackClick={() => navigate(-1)}
          />
        }
        onPrimaryAction={retry}
      />
    );
  if (!restaurant) return null;

  if (!taxRates || taxRates.length === 0)
    return (
      <EmptyScreen
        title={t('taxes.title')}
        emptyText={t('taxes.list.empty')}
        onPrimaryClick={() => setEditMode(true)}
      />
    );

  if (editMode) {
    return (
      <TaxRateForm
        restaurantId={restaurant.id}
        taxRateId={selectedTaxRate?.id}
        onClose={cancelEdit}
        onSuccess={handleFormSuccess}
      />
    );
  }

  const renderMobileCard = (taxRate: TaxRate) => (
    <Card
      key={taxRate.id}
      sx={{
        mb: 2,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        transition: 'background-color 0.2s',
      }}
      onClick={() => handleSelectTaxRate(taxRate)}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" gutterBottom>
              {taxRate.name || 'N/A'}
            </Typography>
            {taxRate.description && !isSmallMobile && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {taxRate.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="primary">
                {taxRate.rate}% rate
              </Typography>
              {taxRate.isDefault && (
                <Chip
                  label={t('common.default')}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleSelectTaxRate(taxRate);
              }}
              aria-label={t('common.edit') + ' ' + (taxRate.name || 'tax rate')}
            >
              <Edit fontSize="small" />
            </IconButton>
            <LoadingButton
              size="small"
              loading={deletingTaxRateId === taxRate.id}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(taxRate.id);
              }}
              color="error"
              aria-label={
                t('common.delete') + ' ' + (taxRate.name || 'tax rate')
              }
              sx={{ minWidth: 'auto', p: 1 }}
            >
              <Delete fontSize="small" />
            </LoadingButton>
          </Box>
        </Box>
        {taxRate.description && isSmallMobile && (
          <Typography variant="body2" color="text.secondary">
            {taxRate.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderDesktopTable = () => (
    <TableContainer component={Paper}>
      <Table
        sx={{ minWidth: isSmallMobile ? 600 : 750 }}
        aria-label="tax rates table"
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('taxes.table.name')}</TableCell>
            <TableCell align="right">{t('taxes.table.rate')}</TableCell>
            {!isSmallMobile && (
              <TableCell align="right">
                {t('taxes.table.description')}
              </TableCell>
            )}
            <TableCell align="right">{t('taxes.table.default')}</TableCell>
            <TableCell align="right">{t('taxes.table.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {taxRates.map((taxRate) => (
            <TableRow
              key={taxRate.id}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              hover
            >
              <TableCell
                component="th"
                scope="row"
                onClick={() => handleSelectTaxRate(taxRate)}
                style={{ cursor: 'pointer' }}
              >
                {taxRate.name || 'N/A'}
              </TableCell>
              <TableCell
                align="right"
                onClick={() => handleSelectTaxRate(taxRate)}
                style={{ cursor: 'pointer' }}
              >
                {taxRate.rate}%
              </TableCell>
              {!isSmallMobile && (
                <TableCell
                  align="right"
                  onClick={() => handleSelectTaxRate(taxRate)}
                  style={{ cursor: 'pointer' }}
                >
                  {taxRate.description || 'N/A'}
                </TableCell>
              )}
              <TableCell
                align="right"
                onClick={() => handleSelectTaxRate(taxRate)}
                style={{ cursor: 'pointer' }}
              >
                {taxRate.isDefault ? t('common.default') : t('common.no')}
              </TableCell>
              <TableCell align="right">
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.25,
                    justifyContent: 'flex-end',
                  }}
                >
                  <IconButton
                    sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectTaxRate(taxRate);
                    }}
                    size="small"
                    aria-label={
                      t('common.edit') + ' ' + (taxRate.name || 'tax rate')
                    }
                  >
                    <Edit />
                  </IconButton>
                  <LoadingButton
                    sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                    loading={deletingTaxRateId === taxRate.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(taxRate.id);
                    }}
                    color="error"
                    size="small"
                    aria-label={
                      t('common.delete') + ' ' + (taxRate.name || 'tax rate')
                    }
                  >
                    <Delete />
                  </LoadingButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <ListTitleBox
        title={t('taxes.title')}
        onPrimaryClick={() => setEditMode(true)}
        primaryButtonText={t('taxes.add-btn')}
        onBackClick={() => navigate(-1)}
      />
      <TaxesSettings />
      {isMobile ? (
        <Box>{taxRates.map((taxRate) => renderMobileCard(taxRate))}</Box>
      ) : (
        renderDesktopTable()
      )}
      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={t('taxes.delete.confirm.title')}
        message={t('taxes.delete.confirm.message')}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
